
import { Box, useMediaQuery } from "@mui/material";
import { color } from "chart.js/helpers";
import { defaults } from "chart.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import zoomPlugin from "chartjs-plugin-zoom";
import { useTheme } from "@emotion/react";
import ZoomControls from "./ZoomControls";
import { formatNumber, formatNumberWithLocale , formatAxisValue} from "../../utils/charts";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels,
    zoomPlugin
);




// Palette used when the chart is pivoted and the series become the years.
const yearColors = [
    "#368dff", "#c53b3b", "#eeb600", "#00bf8e",
    "#8e5ea2", "#ff009f", "#2700ff", "#cfca00",
    "#027602", "#ff00ff", "#22a97c", "#9966ff", "#ffcc99",
];

// Split a long category label into a few short lines: Chart.js renders an array
// of strings as a multi-line tick, which keeps item names readable on the X axis.
const wrapLabel = (label, maxChars = 16, maxLines = 3) => {
    const text = String(label ?? "");
    if (text.length <= maxChars) return text;

    const lines = [];
    let current = "";
    text.split(" ").forEach((word) => {
        if (!current) {
            current = word;
        } else if (`${current} ${word}`.length <= maxChars) {
            current = `${current} ${word}`;
        } else {
            lines.push(current);
            current = word;
        }
    });
    if (current) lines.push(current);

    if (lines.length > maxLines) {
        const kept = lines.slice(0, maxLines - 1);
        kept.push(`${lines[maxLines - 1]}...`);
        return kept;
    }
    return lines;
};

export default function BarChart({ data, showDataLabels, hideSubregions, title, seriesByYear }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    // Some indicators (e.g. SDG 2.3.1) read better with the items on the X axis and one
    // series per year, i.e. the transpose of the default layout. Only the chart is
    // pivoted here: the data table and the downloads keep using the untouched `data`.
    const pivot = Boolean(seriesByYear) && Array.isArray(data.labels?.[0]);
    const multipleAreas = (data.areas?.length ?? 0) > 1;

    // Pivoted, an element the country has no reading for would become an empty category on
    // the X axis rather than a legend entry, so the legend filter below cannot catch it. It is
    // left out of the chart here; the untouched `data` still carries it for the table.
    const pivotDatasets = pivot ? data.datasets.filter((dataset) => !dataset.noData) : [];

    // Create a structured data format
    // Years without a single value would otherwise show up as a legend entry and a row of
    // empty bars (the transform pads missing points with 0), so drop them when pivoting.
    const pivotIndexes = pivot
        ? data.labels
            .map((_, index) => index)
            .filter((index) =>
                pivotDatasets.some((dataset) => {
                    const value = parseFloat(dataset.data[index]);
                    return Number.isFinite(value) && value !== 0;
                })
            )
        : [];

    const structuredData = pivot
        ? {
            labels: pivotDatasets.map((dataset) => wrapLabel(dataset.label)),
            datasets: pivotIndexes.map((index, order) => {
                const [year, area] = data.labels[index];
                const seriesColor = yearColors[order % yearColors.length];

                return {
                    label: multipleAreas ? `${year} - ${area}` : String(year),
                    data: pivotDatasets.map((dataset) => parseFloat(dataset.data[index])),
                    flagDescriptions: pivotDatasets.map(
                        (dataset) => dataset.flagDescriptions?.[index] || ""
                    ),
                    backgroundColor: seriesColor,
                    borderColor: seriesColor,
                    hidden: false,
                };
            }),
        }
        : {
            labels: data.labels.map((label) => label[0]), // extract years from labels
            datasets: data.datasets.map((dataset, index) => ({
                label: dataset.label,
                data: dataset.data.map((value) => parseFloat(value)), // Convert string values to float
                flagDescriptions: dataset.flagDescriptions,
                backgroundColor: dataset.backgroundColor,
                borderColor: dataset.borderColor,
                hidden: hideSubregions ? index !== 0 : false,
                noData: dataset.noData,
            })),
        };


    const options = {
        layout: {
            padding: {
                top: 18,
                right: isMobile ? 8 : 10,
                bottom: 12,
                left: isMobile ? 5 : 10,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "index",
            intersect: false,
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: theme.palette.mode === "dark" ? "#333" : "#e7e7e7",
                },
                title: {
                    display: true,
                    text: data.info.unit,
                    color: theme.palette.primary.fontColor,
                    padding: 0
                },
                ticks: {
                    color: theme.palette.primary.fontColor,
                    padding: 10,
                    // Zooming or panning leaves the axis ending wherever the drag stopped, and Chart.js labels
                    // that end too - 23.7 above 20, 48.3 above 45. Only the regular steps are labelled.
                    includeBounds: false,
                    callback: function (value, index, ticks) {
                        // Shared with the other charts: decimals come from the tick step, so a series
                        // sitting near zero no longer prints the same label three times.
                        return formatAxisValue(value, ticks);
                    },
                },
            },
        },
        animation: {
            duration: 0,
        },
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    padding: isMobile ? 8 : 10,
                    usePointStyle: true,
                    color: theme.palette.primary.fontColor,
                    generateLabels: function (chart) {
                        const original = defaults.plugins.legend.labels.generateLabels;
                        const labelsOriginal = original.call(this, chart);
                        const maxLength = 95; // max length of the legend text

                        return labelsOriginal
                            .map((label) => {
                                // An element the selected country has no reading for still
                                // travels in the data, for the table and the report generator,
                                // but it draws nothing here so it must not sit in the legend.
                                if (chart.data.datasets[label.datasetIndex]?.noData) {
                                    return null;
                                }

                                if (label.hidden && hideSubregions) {
                                    return null;
                                } else if (label.hidden && !hideSubregions) {
                                    label.fontColor =
                                        theme.palette.mode === "light"
                                            ? color(label.fontColor).lighten(2).rgbString()
                                            : "#666";
                                    label.fillStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.fillStyle).lighten(0.5).rgbString()
                                            : "#666";
                                    label.strokeStyle =
                                        theme.palette.mode === "light"
                                            ? color(label.strokeStyle).lighten(0.5).rgbString()
                                            : "#666";
                                } else {
                                    label.fontColor = theme.palette.primary.fontColor;
                                    //   label.fillStyle = label.fillStyle;
                                    //   label.strokeStyle = label.strokeStyle;
                                }

                                // ensure that all items are shown in the legend
                                label.hidden = false;

                                if (label?.text?.length > maxLength) {
                                    label.text = label.text.substring(0, maxLength) + "...";
                                }

                                return label;
                            })
                            .filter((label) => label !== null);
                    },
                },
            },
            datalabels: {
                color: theme.palette.primary.fontColor,
                align: "end",
                anchor: "end",
                clamp: true,
                display: showDataLabels ? "auto" : false,
                backgroundColor: "rgba(255, 255, 255, 0)",
                font: {
                    size: 10,
                },
                padding: 4,
                formatter: (value) => {
                    return isNaN(value) ? '' : formatNumber(value);
                },
            },
            tooltip: {
                filter: (tooltipItem) => {
                    const value = tooltipItem.raw;
                    return value != null && value !== 0;
                },
                callbacks: {
                    label: function (context) {                        
                        const label = context.dataset.label || "";
                        const value = formatNumberWithLocale(context.parsed?.y);
                        const unit = data.info.unit || "";

                        return `${label}: ${value} ${unit}`;
                    },
                    labelTextColor: function () {
                        return "#000";
                    },
                    footer: function (context) {
                        // Chart.js hands this an empty array when the tooltip fires with no
                        // items (data swapped mid-hover, every item filtered out), which used
                        // to crash on context[0].
                        const item = context?.[0];
                        if (!item) return "";
                        const idx = item.dataIndex;
                        const dsIdx = item.datasetIndex;
                        const pointFlag = structuredData.datasets[dsIdx]?.flagDescriptions?.[idx] || "";
                        const flagDescription = pointFlag || data.info.flag_description || "";
                        const value = item.raw;
                        if (value == null || value === 0) return ""
                        return flagDescription ? `${flagDescription}` : "";
                    },
                },
                backgroundColor: "#f1f1f1",
                titleColor: "#000",
                footerColor: "#333",
                footerFont: { weight: "normal" },
                borderColor: "#ccc",
                borderWidth: "1",
            },
            title: {
                display: false,
                text: title,
                font: {
                    size: 14,
                    lineHeight: 1.2,
                },
                padding: { top: 0, left: 0, right: 0, bottom: 30 },
            },
            zoom: {
                limits: {
                    x: {
                        min: 'original',
                        max: 'original',
                        minRange: 2
                    },
                    y: {
                        min: 'original',
                        max: 'original',
                        minRange: 0.1
                    }
                },
                zoom: {
                    wheel: {
                        enabled: false,
                    },
                    pinch: {
                        enabled: false
                    },
                    drag: {
                        enabled: true,
                        backgroundColor: 'rgba(225,225,225,0.3)',
                        borderColor: 'rgb(225,225,225)',
                        borderWidth: 1,
                        threshold: 100,
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'xy',
                    rangeMin: {
                        x: null,
                        y: null
                    },
                    rangeMax: {
                        x: null,
                        y: null
                    }
                }
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Bar options={options} data={structuredData} ref={ref} />
            <ZoomControls chartRef={ref} data={structuredData} />
        </Box>
    );
}
